import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import './FourPointRigging.css';

export default function FourPointRigging() {
  // Inputs matching MHTML / Excel defaults
  const [W, setW] = useState(20);
  const [WLL_sling, setWLLSling] = useState(10);
  const [WLL_shackle, setWLLShackle] = useState(8.5);

  const [D1, setD1] = useState(4.621);
  const [D2, setD2] = useState(5.000);
  const [D3, setD3] = useState(2.980);
  const [D4, setD4] = useState(3.000);

  const [DT1, setDT1] = useState(9.621);
  const [DT2, setDT2] = useState(5.980);

  const [S1, setS1] = useState(10.146);
  const [S2, setS2] = useState(10.146);
  const [S3, setS3] = useState(10.324);
  const [S4, setS4] = useState(10.324);

  const [RW1, setRW1] = useState(0.150);
  const [RW2, setRW2] = useState(0.150);
  const [RW3, setRW3] = useState(0.165);
  const [RW4, setRW4] = useState(0.165);

  // Results State
  const [results, setResults] = useState({
    T1: 0, T2: 0, T3: 0, T4: 0,
    L1: 0, L2: 0, L3: 0, L4: 0,
    SF_sling: 0, SF_shackle: 0,
    maxTension: 0
  });

  const canvasRef = useRef(null);

  // Sync Distances: DT1 = D1 + D2, DT2 = D3 + D4
  // Triggered when individual distances D1, D2, D3, D4 change
  const handleDChange = (name, value) => {
    const val = parseFloat(value) || 0;
    if (name === 'D1') {
      setD1(val);
      setDT1(Number((val + D2).toFixed(3)));
    } else if (name === 'D2') {
      setD2(val);
      setDT1(Number((D1 + val).toFixed(3)));
    } else if (name === 'D3') {
      setD3(val);
      setDT2(Number((val + D4).toFixed(3)));
    } else if (name === 'D4') {
      setD4(val);
      setDT2(Number((D3 + val).toFixed(3)));
    }
  };

  // Triggered when total span DT1, DT2 changes (syncs LP3/LP4 distances)
  const handleDTChange = (name, value) => {
    const val = parseFloat(value) || 0;
    if (name === 'DT1') {
      setDT1(val);
      setD2(Number((val - D1).toFixed(3)));
    } else if (name === 'DT2') {
      setDT2(val);
      setD4(Number((val - D3).toFixed(3)));
    }
  };

  // Perform calculations on input change
  useEffect(() => {
    // 1. Vertical load distributions (incorporating rigging weights directly on each leg)
    const L1 = (W + RW1) * (D2 / DT1) * (D4 / DT2);
    const L2 = (W + RW2) * (D2 / DT1) * (D3 / DT2);
    const L3 = (W + RW3) * (D1 / DT1) * (D4 / DT2);
    const L4 = (W + RW4) * (D1 / DT1) * (D3 / DT2);

    // Helper for 3D Tension: T = L * (S / H) where H = sqrt(S^2 - (d1^2 + d2^2))
    const calcTension = (load, sling, dist1, dist2) => {
      const denomSq = sling * sling - (dist1 * dist1 + dist2 * dist2);
      if (denomSq <= 0) return 0;
      return load * (sling / Math.sqrt(denomSq));
    };

    const T1 = calcTension(L1, S1, D1, D3);
    const T2 = calcTension(L2, S2, D1, D4);
    const T3 = calcTension(L3, S3, D2, D3);
    const T4 = calcTension(L4, S4, D2, D4);

    const maxTension = Math.max(T1, T2, T3, T4);
    const SF_sling = WLL_sling / (maxTension || 1);
    const SF_shackle = WLL_shackle / (maxTension || 1);

    setResults({
      T1: Number(T1.toFixed(3)),
      T2: Number(T2.toFixed(3)),
      T3: Number(T3.toFixed(3)),
      T4: Number(T4.toFixed(3)),
      L1: Number(L1.toFixed(3)),
      L2: Number(L2.toFixed(3)),
      L3: Number(L3.toFixed(3)),
      L4: Number(L4.toFixed(3)),
      SF_sling: Number(SF_sling.toFixed(3)),
      SF_shackle: Number(SF_shackle.toFixed(3)),
      maxTension: Number(maxTension.toFixed(3))
    });
  }, [W, WLL_sling, WLL_shackle, D1, D2, D3, D4, DT1, DT2, S1, S2, S3, S4, RW1, RW2, RW3, RW4]);

  // Render Canvas COG Diagram
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Padding & dimensions
    const pad = 40;
    const cw = canvas.width;
    const ch = canvas.height;

    // Draw background grid lines
    ctx.strokeStyle = 'rgba(26, 86, 219, 0.07)';
    ctx.lineWidth = 1;
    for (let x = 20; x < cw; x += 20) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, ch);
      ctx.stroke();
    }
    for (let y = 20; y < ch; y += 20) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(cw, y);
      ctx.stroke();
    }

    // Scale calculation
    const scaleX = (cw - 2 * pad) / (DT1 || 1);
    const scaleY = (ch - 2 * pad) / (DT2 || 1);
    const scale = Math.min(scaleX, scaleY);

    const rectW = DT1 * scale;
    const rectH = DT2 * scale;
    const offsetX = (cw - rectW) / 2;
    const offsetY = (ch - rectH) / 2;

    // Draw main frame rectangle
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
    ctx.lineWidth = 2;
    ctx.strokeRect(offsetX, offsetY, rectW, rectH);

    // Corner Positions (Lifting Points)
    const points = [
      { id: 'LP1', x: offsetX, y: offsetY, load: results.L1 },
      { id: 'LP2', x: offsetX + rectW, y: offsetY, load: results.L2 },
      { id: 'LP3', x: offsetX, y: offsetY + rectH, load: results.L3 },
      { id: 'LP4', x: offsetX + rectW, y: offsetY + rectH, load: results.L4 }
    ];

    // Draw Corner Circles & Labels
    points.forEach(pt => {
      ctx.fillStyle = '#1a56db';
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 6, 0, 2 * Math.PI);
      ctx.fill();

      ctx.fillStyle = '#6b6760';
      ctx.font = '10px monospace';
      ctx.fillText(pt.id, pt.x - 10, pt.y - 12);
      ctx.fillText(`${pt.load.toFixed(3)}t`, pt.x - 15, pt.y + 20);
    });

    // Draw COG Target
    const cogX = offsetX + D1 * scale;
    const cogY = offsetY + D3 * scale;

    ctx.strokeStyle = '#e8463a';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cogX, cogY, 10, 0, 2 * Math.PI);
    ctx.stroke();

    ctx.fillStyle = '#e8463a';
    ctx.beginPath();
    ctx.arc(cogX, cogY, 3, 0, 2 * Math.PI);
    ctx.fill();

    // COG Label
    ctx.fillStyle = '#0a0a0a';
    ctx.font = 'bold 11px sans-serif';
    ctx.fillText('COG', cogX + 15, cogY + 4);

  }, [D1, D3, DT1, DT2, results]);

  const getSfBadgeClass = (sf) => {
    if (sf >= 2) return 'badge-safe';
    if (sf >= 1.25) return 'badge-warn';
    return 'badge-danger';
  };

  const getSfBadgeLabel = (sf) => {
    if (sf >= 2) return 'SAFE';
    if (sf >= 1.25) return 'MARGINAL';
    return 'UNSAFE';
  };

  const getSfColor = (sf) => {
    if (sf >= 2) return '#0d7a5f';
    if (sf >= 1.25) return '#d97706';
    return '#dc2626';
  };

  return (
    <div className="rigging-landing">
      <Helmet>
        <title>4-Point Rigging Calculator — Professional Lift Planning Tool</title>
        <meta name="description" content="Calculate sling tensions, load distribution, and safety factors for asymmetric 4-point lifts. Real-time results with COG positioning and rigging weight analysis." />
      </Helmet>
      {/* HERO */}
      <section className="hero" id="home">
        <div className="hero-grid-bg"></div>
        <div className="hero-glow"></div>
        <div className="hero-tag">Rigging Engineering Tool</div>
        <h1>
          4-Point<br />
          <span className="accent">Rigging</span><br />
          <span className="stroke">Calculator</span>
        </h1>
        <p className="hero-desc">
          Professional asymmetric load distribution calculator for heavy lift operations. Compute sling tensions, lifting point loads, and safety factors in real time.
        </p>
        <div className="hero-buttons">
          <a href="#calculator" className="btn-primary">Launch Calculator</a>
          <a href="" className="btn"></a>
          <a href="#how" className="btn-outline">Learn How It Works</a>
        </div>
        <div className="hero-stats">
          <div className="stat-item">
            <div className="stat-num">4<span>×</span></div>
            <div className="stat-label">Lifting Points</div>
          </div>
          <div className="stat-item">
            <div className="stat-num"><span>∞</span></div>
            <div className="stat-label">COG Positions</div>
          </div>
          <div className="stat-item">
            <div className="stat-num">SF<span>≥2</span></div>
            <div className="stat-label">Safety Standard</div>
          </div>
          <div className="stat-item">
            <div className="stat-num"><span>±</span>0.001</div>
            <div className="stat-label">Precision (tonnes)</div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how">
        <div className="section-tag">Process</div>
        <h2 className="section-title">How It<br />Works</h2>
        <p className="section-sub">A systematic approach to asymmetric 4-point lift planning using moment distribution and geometric sling analysis.</p>
        <div className="steps">
          <div className="step">
            <div className="step-num">01</div>
            <div className="step-icon">⚖</div>
            <h3>Define Cargo</h3>
            <p>Input total cargo weight (W) in tonnes. This is the gross weight including all permanently attached components before rigging equipment is added.</p>
          </div>
          <div className="step">
            <div className="step-num">02</div>
            <div className="step-icon">✦</div>
            <h3>Locate COG</h3>
            <p>Measure distances from the Center of Gravity (COG) to each lifting point in both the longitudinal (D1, D2) and lateral (D3, D4) directions.</p>
          </div>
          <div className="step">
            <div className="step-num">03</div>
            <div className="step-icon">◈</div>
            <h3>Input Slings</h3>
            <p>Enter sling lengths S1–S4 for each leg. Longer slings reduce the sling angle, which reduces horizontal forces on the lifting lugs.</p>
          </div>
          <div className="step">
            <div className="step-num">04</div>
            <div className="step-icon">⬡</div>
            <h3>Add Rigging Weights</h3>
            <p>Input estimated rigging equipment weight (RW1–RW4) per leg — shackles, slings, and hardware contribute to sling tension calculations.</p>
          </div>
          <div className="step">
            <div className="step-num">05</div>
            <div className="step-icon">✓</div>
            <h3>Verify Safety</h3>
            <p>Review calculated sling tensions against WLL ratings. Safety factors ≥ 2.0 are required by most lifting standards (ASME B30.9, EN 13414).</p>
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator">
        <div className="section-tag">Tool</div>
        <h2 className="section-title">Live<br />Calculator</h2>
        <p className="section-sub">All results update instantly as you type. Pre-loaded with a sample 20-tonne asymmetric lift.</p>

        <div className="calc-layout">
          {/* LEFT: INPUTS */}
          <div>
            <div className="calc-panel">
              <div className="panel-title">01 — Cargo &amp; WLL Limits</div>
              <div className="input-grid">
                <div className="input-group">
                  <div className="input-label">W — Weight of Cargo</div>
                  <div className="input-wrap">
                    <input type="number" value={W} onChange={e => setW(parseFloat(e.target.value) || 0)} step="0.001" />
                    <span className="unit-tag">t</span>
                  </div>
                </div>
                <div></div>
                <div className="input-group">
                  <div className="input-label">WLL — Sling</div>
                  <div className="input-wrap">
                    <input type="number" value={WLL_sling} onChange={e => setWLLSling(parseFloat(e.target.value) || 0)} step="0.1" />
                    <span className="unit-tag">t</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">WLL — Shackle</div>
                  <div className="input-wrap">
                    <input type="number" value={WLL_shackle} onChange={e => setWLLShackle(parseFloat(e.target.value) || 0)} step="0.1" />
                    <span className="unit-tag">t</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="calc-panel" style={{ marginTop: '1rem' }}>
              <div className="panel-title">02 — COG Distances</div>
              <div className="input-grid">
                <div className="input-group">
                  <div className="input-label">D1 — COG to LP1 (longit.)</div>
                  <div className="input-wrap">
                    <input type="number" value={D1} onChange={e => handleDChange('D1', e.target.value)} step="0.001" />
                    <span className="unit-tag">m</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">D2 — COG to LP3 (longit.)</div>
                  <div className="input-wrap">
                    <input type="number" value={D2} onChange={e => handleDChange('D2', e.target.value)} step="0.001" />
                    <span className="unit-tag">m</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">D3 — COG to LP1&amp;3 (lat.)</div>
                  <div className="input-wrap">
                    <input type="number" value={D3} onChange={e => handleDChange('D3', e.target.value)} step="0.001" />
                    <span className="unit-tag">m</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">D4 — COG to LP2&amp;4 (lat.)</div>
                  <div className="input-wrap">
                    <input type="number" value={D4} onChange={e => handleDChange('D4', e.target.value)} step="0.001" />
                    <span className="unit-tag">m</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">DT1 — Total Span (D1+D2)</div>
                  <div className="input-wrap">
                    <input type="number" value={DT1} onChange={e => handleDTChange('DT1', e.target.value)} step="0.001" />
                    <span className="unit-tag">m</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">DT2 — Total Span (D3+D4)</div>
                  <div className="input-wrap">
                    <input type="number" value={DT2} onChange={e => handleDTChange('DT2', e.target.value)} step="0.001" />
                    <span className="unit-tag">m</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="calc-panel" style={{ marginTop: '1rem' }}>
              <div className="panel-title">03 — Sling Lengths &amp; Rigging Weights</div>
              <div className="input-grid">
                <div className="input-group">
                  <div className="input-label">S1 — Sling Length L1</div>
                  <div className="input-wrap">
                    <input type="number" value={S1} onChange={e => setS1(parseFloat(e.target.value) || 0)} step="0.001" />
                    <span className="unit-tag">m</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">RW1 — Rigging Weight</div>
                  <div className="input-wrap">
                    <input type="number" value={RW1} onChange={e => setRW1(parseFloat(e.target.value) || 0)} step="0.001" />
                    <span className="unit-tag">t</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">S2 — Sling Length L2</div>
                  <div className="input-wrap">
                    <input type="number" value={S2} onChange={e => setS2(parseFloat(e.target.value) || 0)} step="0.001" />
                    <span className="unit-tag">m</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">RW2 — Rigging Weight</div>
                  <div className="input-wrap">
                    <input type="number" value={RW2} onChange={e => setRW2(parseFloat(e.target.value) || 0)} step="0.001" />
                    <span className="unit-tag">t</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">S3 — Sling Length L3</div>
                  <div className="input-wrap">
                    <input type="number" value={S3} onChange={e => setS3(parseFloat(e.target.value) || 0)} step="0.001" />
                    <span className="unit-tag">m</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">RW3 — Rigging Weight</div>
                  <div className="input-wrap">
                    <input type="number" value={RW3} onChange={e => setRW3(parseFloat(e.target.value) || 0)} step="0.001" />
                    <span className="unit-tag">t</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">S4 — Sling Length L4</div>
                  <div className="input-wrap">
                    <input type="number" value={S4} onChange={e => setS4(parseFloat(e.target.value) || 0)} step="0.001" />
                    <span className="unit-tag">m</span>
                  </div>
                </div>
                <div className="input-group">
                  <div className="input-label">RW4 — Rigging Weight</div>
                  <div className="input-wrap">
                    <input type="number" value={RW4} onChange={e => setRW4(parseFloat(e.target.value) || 0)} step="0.001" />
                    <span className="unit-tag">t</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: RESULTS */}
          <div>
            <div className="calc-panel">
              <div className="panel-title">Sling Tensions</div>
              <div className="results-grid">
                <div className="result-box">
                  <div className="result-box-label">Sling 1</div>
                  <div className="result-box-val" id="T1">{results.T1.toFixed(3)}<span class="result-box-unit">t</span></div>
                </div>
                <div className="result-box">
                  <div className="result-box-label">Sling 2</div>
                  <div className="result-box-val" id="T2">{results.T2.toFixed(3)}<span class="result-box-unit">t</span></div>
                </div>
                <div className="result-box">
                  <div className="result-box-label">Sling 3</div>
                  <div className="result-box-val" id="T3">{results.T3.toFixed(3)}<span class="result-box-unit">t</span></div>
                </div>
                <div className="result-box">
                  <div className="result-box-label">Sling 4</div>
                  <div className="result-box-val" id="T4">{results.T4.toFixed(3)}<span class="result-box-unit">t</span></div>
                </div>
              </div>

              <div className="divider-line"></div>
              <div className="panel-title">Safety Factors</div>
              <div className="sf-block">
                <div className="sf-row">
                  <div>
                    <div className="sf-name">Sling SF</div>
                    <div className="sf-val" id="SF_sling" style={{ color: getSfColor(results.SF_sling) }}>{results.SF_sling.toFixed(3)}</div>
                  </div>
                  <span className={`sf-badge ${getSfBadgeClass(results.SF_sling)}`} id="SF_sling_badge">{getSfBadgeLabel(results.SF_sling)}</span>
                </div>
                <div className="sf-row">
                  <div>
                    <div className="sf-name">Shackle SF</div>
                    <div className="sf-val" id="SF_shackle" style={{ color: getSfColor(results.SF_shackle) }}>{results.SF_shackle.toFixed(3)}</div>
                  </div>
                  <span className={`sf-badge ${getSfBadgeClass(results.SF_shackle)}`} id="SF_shackle_badge">{getSfBadgeLabel(results.SF_shackle)}</span>
                </div>
              </div>

              <div className="divider-line"></div>
              <div className="panel-title">Load at Lifting Points</div>
              <table className="load-table">
                <thead><tr><th>Point</th><th>Description</th><th style={{ textAlign: 'right' }}>Load</th></tr></thead>
                <tbody>
                  <tr><td>LP1</td><td style={{ color: 'var(--text2)', fontSize: '13px' }}>Lifting Point 1</td><td id="L1">{results.L1.toFixed(3)} t</td></tr>
                  <tr><td>LP2</td><td style={{ color: 'var(--text2)', fontSize: '13px' }}>Lifting Point 2</td><td id="L2">{results.L2.toFixed(3)} t</td></tr>
                  <tr><td>LP3</td><td style={{ color: 'var(--text2)', fontSize: '13px' }}>Lifting Point 3</td><td id="L3">{results.L3.toFixed(3)} t</td></tr>
                  <tr><td>LP4</td><td style={{ color: 'var(--text2)', fontSize: '13px' }}>Lifting Point 4</td><td id="L4">{results.L4.toFixed(3)} t</td></tr>
                </tbody>
              </table>

              <div className="canvas-wrap">
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px' }}>COG Position Diagram</div>
                <canvas ref={canvasRef} id="diag" height="269" width="390"></canvas>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FORMULAS */}
      <section id="formulas">
        <div className="section-tag">Reference</div>
        <h2 className="section-title">Calculation<br />Formulas</h2>
        <p className="section-sub">All equations used in this calculator, based on static equilibrium moment distribution for asymmetric 4-point lifts.</p>
        <div className="formula-grid">
          <div className="formula-card">
            <div className="formula-card-tag">Load Distribution</div>
            <h3>Load at Lifting Point 1</h3>
            <div className="formula-eq">L1 = (W + RW1) × (D2 / DT1) × (D4 / DT2)</div>
            <p className="formula-desc">Distributes total weight including rigging weight using the inverse lever rule in both longitudinal and lateral directions from the COG to each lifting point.</p>
          </div>
          <div className="formula-card">
            <div className="formula-card-tag">Load Distribution</div>
            <h3>Load at Lifting Point 2</h3>
            <div className="formula-eq">L2 = (W + RW2) × (D2 / DT1) × (D3 / DT2)</div>
            <p className="formula-desc">LP2 and LP1 share the same longitudinal factor (D2/DT1) but differ in the lateral distribution between the two sides.</p>
          </div>
          <div className="formula-card">
            <div className="formula-card-tag">Load Distribution</div>
            <h3>Load at Lifting Points 3 &amp; 4</h3>
            <div className="formula-eq">L3 = (W + RW3) × (D1 / DT1) × (D4 / DT2)<br />L4 = (W + RW4) × (D1 / DT1) × (D3 / DT2)</div>
            <p className="formula-desc">The farther a lifting point is from the COG, the smaller its share. The sum of all four loads equals the total cargo weight plus rigging equipment.</p>
          </div>
          <div className="formula-card">
            <div className="formula-card-tag">Tension</div>
            <h3>Sling Tension</h3>
            <div className="formula-eq">T_n = L_n * S_n / SQRT(S_n^2 - (dist1^2 + dist2^2))</div>
            <p className="formula-desc">Sling tension accounts for both the vertical load at the lifting point and the 3D inclination angle factor calculated from horizontal offsets to the hook position.</p>
          </div>
          <div className="formula-card">
            <div className="formula-card-tag">Safety Check</div>
            <h3>Safety Factor — Sling</h3>
            <div className="formula-eq">SF_sling = WLL_sling / max(T1, T2, T3, T4)</div>
            <p className="formula-desc">The safety factor is checked against the maximum (governing) sling tension. Per ASME B30.9 and most DNV/Lloyd's standards, SF ≥ 2.0 is required for most lifts.</p>
          </div>
          <div className="formula-card">
            <div className="formula-card-tag">Safety Check</div>
            <h3>Safety Factor — Shackle</h3>
            <div className="formula-eq">SF_shackle = WLL_shackle / max(T1, T2, T3, T4)</div>
            <p className="formula-desc">Shackles typically have lower WLL than slings of equivalent capacity. The governing tension is used for both checks — the shackle SF is always the more conservative value.</p>
          </div>
        </div>
      </section>

      {/* SAFETY GUIDE */}
      <section id="safety">
        <div className="section-tag">Guidelines</div>
        <h2 className="section-title">Safety<br />Reference</h2>
        <p className="section-sub">Industry safety standards and best practices for multi-point rigging operations and heavy lifts.</p>
        <div className="safety-grid">
          <div className="safety-card ok">
            <div className="safety-card-icon">✓</div>
            <h3>Safety Factor Requirements</h3>
            <p>Minimum safety factors for rigging components under most international lifting standards:</p>
            <ul>
              <li>Wire rope slings: SF ≥ 5:1 (proof), 2:1 working</li>
              <li>Synthetic slings (EN 1492): SF ≥ 7:1</li>
              <li>Shackles (ASME B30.26): SF ≥ 5:1</li>
              <li>Lifting beams / spreader bars: SF ≥ 5:1</li>
              <li>Dynamic lifts (offshore, shock loading): SF ≥ 2.0 on WLL</li>
            </ul>
          </div>
          <div className="safety-card warn">
            <div className="safety-card-icon">⚠</div>
            <h3>COG Uncertainty</h3>
            <p>COG location is rarely perfectly known. Industry practice requires:</p>
            <ul>
              <li>Apply ±5% COG uncertainty for fabricated steel</li>
              <li>±10% for complex assemblies or estimated weights</li>
              <li>Use COG envelope method — check worst-case corner positions</li>
              <li>Dynamic amplification factor (DAF) of 1.05–1.3 for crane lifts</li>
              <li>Re-verify COG after any late modifications</li>
            </ul>
          </div>
          <div className="safety-card danger">
            <div className="safety-card-icon">✕</div>
            <h3>Never Exceed WLL</h3>
            <p>Working Load Limits must never be exceeded under any circumstance:</p>
            <ul>
              <li>Inspect all slings and shackles before each use</li>
              <li>Discard slings with broken wires, kinks, or abrasion damage</li>
              <li>Never shock-load rigging equipment</li>
              <li>Reduce WLL for multi-leg slings with angles &gt; 60° from vertical</li>
              <li>Temperature extremes reduce synthetic sling capacity</li>
            </ul>
          </div>
          <div className="safety-card info">
            <div className="safety-card-icon">ℹ</div>
            <h3>Relevant Standards</h3>
            <p>Applicable codes and standards for rigging and lifting operations:</p>
            <ul>
              <li>ASME B30.9 — Slings</li>
              <li>ASME B30.26 — Rigging Hardware</li>
              <li>EN 13414 — Steel wire rope slings</li>
              <li>DNV-ST-N001 — Marine Operations</li>
              <li>ISO 4308 — Cranes — Selection of wire ropes</li>
              <li>LEEA 002 — Code of Practice for Lifting Equipment</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about">
        <div className="section-tag">About</div>
        <h2 className="section-title">About This<br />Tool</h2>
        <div className="about-layout">
          <div className="about-text">
            <p>This 4-Point Rigging Calculator is designed for rigging engineers, lift supervisors, and project engineers involved in heavy lift planning. It solves the asymmetric load distribution problem for lifts where the center of gravity does not coincide with the geometric center of the lifting arrangement.</p>
            <p>The underlying method uses static equilibrium — distributing the total cargo weight to each lifting point using the inverse lever rule applied in two orthogonal directions. This is the standard industry method for preliminary lift studies and is consistent with calculations used in certified lifting plans.</p>
            <p>All calculations should be verified by a qualified lifting engineer before use in any actual lifting operation. This tool is intended as a design aid and planning tool, not a substitute for a certified lift plan.</p>
            <p>Based on the methodology demonstrated in the accompanying technical tutorial video, with pre-loaded sample data from a 20-tonne asymmetric lift scenario.</p>
          </div>
          <div className="about-specs">
            <div style={{ fontFamily: 'var(--font-head)', fontSize: '13px', fontWeight: '600', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid var(--border)' }}>Tool Specifications</div>
            <div className="spec-row"><span className="spec-key">Lift Type</span><span class="spec-val">4-point, asymmetric COG</span></div>
            <div className="spec-row"><span class="spec-key">Method</span><span class="spec-val">Static moment distribution</span></div>
            <div className="spec-row"><span class="spec-key">Input Precision</span><span class="spec-val accent">0.001 t / 0.001 m</span></div>
            <div className="spec-row"><span class="spec-key">Output Precision</span><span class="spec-val accent">3 decimal places</span></div>
            <div className="spec-row"><span class="spec-key">Weight Unit</span><span class="spec-val">Metric tonnes (t)</span></div>
            <div className="spec-row"><span class="spec-key">Distance Unit</span><span class="spec-val">Metres (m)</span></div>
            <div className="spec-row"><span class="spec-key">Safe SF Threshold</span><span class="spec-val accent">≥ 2.00</span></div>
            <div className="spec-row"><span class="spec-key">Marginal SF</span><span class="spec-val accent">1.25 – 1.99</span></div>
            <div className="spec-row"><span class="spec-key">Unsafe SF</span><span class="spec-val" style={{ color: 'var(--danger)' }}>&lt; 1.25</span></div>
            <div className="spec-row"><span class="spec-key">Update Mode</span><span class="spec-val">Real-time (on input)</span></div>
          </div>
        </div>
      </section>
    </div>
  );
}
